/**
 * SOLID Review
 * S — Strong. One concern: declare what ComponentFrontendApplication supplies — `ClientUseCases` facing
 *     the Framework and `ClientServerEvents` facing Infrastructure. No state beyond the ports, no logic.
 * O — Good. A new context's use cases are new forwards in `ClientUseCasesPort`; the ports do not change.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. The Framework can reach only `ClientUseCases`; Infrastructure only `deliver`.
 * D — Strong. The kernel, the two contracts and a structural surfaces type that never names the
 *     component (see `TApplicationSurfaces`).
 */
import { ProvidedPort, PortHolder } from '@ml3d/kernel';
import { ClientUseCasesContract, type IPortClientUseCases } from '../ports/ClientUseCases.contract';
import { ClientServerEventsContract, type IPortClientServerEvents } from '../ports/ClientServerEvents.contract';
import type { TableContext } from '../table/TableContext';
import type { SessionContext } from '../session/SessionContext';
import type { AuthContext } from '../auth/AuthContext';
import type { LegalContext } from '../legal/LegalContext';
import type { PaymentContext } from '../payment/PaymentContext';
import type { AdsContext } from '../ads/AdsContext';
import type { HealthContext } from '../health/HealthContext';
import type { MediaContext } from '../media/MediaContext';
import type { IPayloadClientServerEvent, TTableEvent } from '../ports/ClientServerEvents.contract';

/**
 * What the two lollipops reach, which ComponentFrontendApplication satisfies structurally.
 *
 * ⚠ NEVER `ComponentFrontendApplication` itself: naming the component here, even `import type`, closes
 * a module cycle `no-circular` rejects, because the component imports this file for its value
 * (the backend's `TApplicationSurfaces`, same reason).
 */
export interface TApplicationSurfaces {
  readonly table: TableContext;
  readonly session: SessionContext;
  readonly auth: AuthContext;
  readonly legal: LegalContext;
  readonly payment: PaymentContext;
  readonly ads: AdsContext;
  readonly health: HealthContext;
  readonly media: MediaContext;
}

/**
 * The Framework's lollipop: each op forwards to the context that owns it, READ AT CALL TIME — the
 * contexts are built in `build()`, one phase after this object exists.
 */
class ClientUseCasesPort implements IPortClientUseCases {
  constructor(private readonly surfaces: TApplicationSurfaces) {}

  startGame: IPortClientUseCases['startGame'] = (ctx, command) => this.surfaces.table.startGame(ctx, command);
  rollDice: IPortClientUseCases['rollDice'] = (ctx, command) => this.surfaces.table.rollDice(ctx, command);
  peekDice: IPortClientUseCases['peekDice'] = (ctx, command) => this.surfaces.table.peekDice(ctx, command);
  announce: IPortClientUseCases['announce'] = (ctx, command) => this.surfaces.table.announce(ctx, command);
  believe: IPortClientUseCases['believe'] = (ctx, command) => this.surfaces.table.believe(ctx, command);
  liftCup: IPortClientUseCases['liftCup'] = (ctx, command) => this.surfaces.table.liftCup(ctx, command);
  rollFirst: IPortClientUseCases['rollFirst'] = (ctx, command) => this.surfaces.table.rollFirst(ctx, command);
  playAgain: IPortClientUseCases['playAgain'] = (ctx, command) => this.surfaces.table.playAgain(ctx, command);
  leaveTable: IPortClientUseCases['leaveTable'] = (ctx, command) => this.surfaces.table.leaveTable(ctx, command);
  loadLobbyRoom: IPortClientUseCases['loadLobbyRoom'] = (ctx, command) => this.surfaces.table.loadLobbyRoom(ctx, command);
  lobbyPollTick: IPortClientUseCases['lobbyPollTick'] = (ctx, command) => this.surfaces.table.lobbyPollTick(ctx, command);
  watchTable: IPortClientUseCases['watchTable'] = (ctx, command) => this.surfaces.table.watchTable(ctx, command);
  unwatchTable: IPortClientUseCases['unwatchTable'] = (ctx, command) => this.surfaces.table.unwatchTable(ctx, command);
  startSession: IPortClientUseCases['startSession'] = (ctx, command) => this.surfaces.session.startSession(ctx, command);
  enterGame: IPortClientUseCases['enterGame'] = (ctx, command) => this.surfaces.session.enterGame(ctx, command);
  createGuest: IPortClientUseCases['createGuest'] = (ctx, command) => this.surfaces.session.createGuest(ctx, command);
  checkTableCode: IPortClientUseCases['checkTableCode'] = (ctx, command) => this.surfaces.session.checkTableCode(ctx, command);
  navigate: IPortClientUseCases['navigate'] = (ctx, command) => this.surfaces.session.navigate(ctx, command);
  dismissJoinError: IPortClientUseCases['dismissJoinError'] = (ctx, command) => this.surfaces.session.dismissJoinError(ctx, command);
  requestLoginCode: IPortClientUseCases['requestLoginCode'] = (ctx, command) => this.surfaces.auth.requestLoginCode(ctx, command);
  signUp: IPortClientUseCases['signUp'] = (ctx, command) => this.surfaces.auth.signUp(ctx, command);
  verifyCode: IPortClientUseCases['verifyCode'] = (ctx, command) => this.surfaces.auth.verifyCode(ctx, command);
  checkReferral: IPortClientUseCases['checkReferral'] = (ctx, command) => this.surfaces.auth.checkReferral(ctx, command);
  loadProfile: IPortClientUseCases['loadProfile'] = (ctx, command) => this.surfaces.auth.loadProfile(ctx, command);
  changeNickname: IPortClientUseCases['changeNickname'] = (ctx, command) => this.surfaces.auth.changeNickname(ctx, command);
  requestEmailChange: IPortClientUseCases['requestEmailChange'] = (ctx, command) => this.surfaces.auth.requestEmailChange(ctx, command);
  verifyEmailChange: IPortClientUseCases['verifyEmailChange'] = (ctx, command) => this.surfaces.auth.verifyEmailChange(ctx, command);
  signOut: IPortClientUseCases['signOut'] = (ctx, command) => this.surfaces.auth.signOut(ctx, command);
  loadLegalDocuments: IPortClientUseCases['loadLegalDocuments'] = (ctx, command) => this.surfaces.legal.loadLegalDocuments(ctx, command);
  checkLegalReview: IPortClientUseCases['checkLegalReview'] = (ctx, command) => this.surfaces.legal.checkLegalReview(ctx, command);
  acceptLegalTerms: IPortClientUseCases['acceptLegalTerms'] = (ctx, command) => this.surfaces.legal.acceptLegalTerms(ctx, command);
  exportMyData: IPortClientUseCases['exportMyData'] = (ctx, command) => this.surfaces.legal.exportMyData(ctx, command);
  requestAccountDeletion: IPortClientUseCases['requestAccountDeletion'] = (ctx, command) => this.surfaces.legal.requestAccountDeletion(ctx, command);
  deleteAccount: IPortClientUseCases['deleteAccount'] = (ctx, command) => this.surfaces.legal.deleteAccount(ctx, command);
  startConsent: IPortClientUseCases['startConsent'] = (ctx, command) => this.surfaces.legal.startConsent(ctx, command);
  openCookiePreferences: IPortClientUseCases['openCookiePreferences'] = (ctx, command) => this.surfaces.legal.openCookiePreferences(ctx, command);
  saveCookiePreferences: IPortClientUseCases['saveCookiePreferences'] = (ctx, command) => this.surfaces.legal.saveCookiePreferences(ctx, command);
  startCheckout: IPortClientUseCases['startCheckout'] = (ctx, command) => this.surfaces.payment.startCheckout(ctx, command);
  readBalance: IPortClientUseCases['readBalance'] = (ctx, command) => this.surfaces.payment.readBalance(ctx, command);
  watchAccount: IPortClientUseCases['watchAccount'] = (ctx, command) => this.surfaces.payment.watchAccount(ctx, command);
  unwatchAccount: IPortClientUseCases['unwatchAccount'] = (ctx, command) => this.surfaces.payment.unwatchAccount(ctx, command);
  loadAdConfig: IPortClientUseCases['loadAdConfig'] = (ctx, command) => this.surfaces.ads.loadAdConfig(ctx, command);
  checkServerHealth: IPortClientUseCases['checkServerHealth'] = (ctx, command) => this.surfaces.health.checkServerHealth(ctx, command);
  toggleMedia: IPortClientUseCases['toggleMedia'] = (ctx, command) => this.surfaces.media.toggleMedia(ctx, command);
  reconnectMedia: IPortClientUseCases['reconnectMedia'] = (ctx, command) => this.surfaces.media.reconnectMedia(ctx, command);
  checkMediaIdle: IPortClientUseCases['checkMediaIdle'] = (ctx, command) => this.surfaces.media.checkMediaIdle(ctx, command);
  mediaRemovedByServer: IPortClientUseCases['mediaRemovedByServer'] = (ctx, command) => this.surfaces.media.mediaRemovedByServer(ctx, command);
  dismissMediaBanner: IPortClientUseCases['dismissMediaBanner'] = (ctx, command) => this.surfaces.media.dismissMediaBanner(ctx, command);
  mediaUsed: IPortClientUseCases['mediaUsed'] = (ctx, command) => this.surfaces.media.mediaUsed(ctx, command);
  mediaToken: IPortClientUseCases['mediaToken'] = (ctx, command) => this.surfaces.media.mediaToken(ctx, command);
  reportMediaConnection: IPortClientUseCases['reportMediaConnection'] = (ctx, command) => this.surfaces.media.reportMediaConnection(ctx, command);
  checkMediaHealth: IPortClientUseCases['checkMediaHealth'] = (ctx, command) => this.surfaces.media.checkMediaHealth(ctx, command);
  loadMediaUsageReport: IPortClientUseCases['loadMediaUsageReport'] = (ctx, command) => this.surfaces.media.loadMediaUsageReport(ctx, command);
}

/**
 * What ComponentFrontendApplication supplies (frontend strict-ports plan, FE1, 2026-10-03) — the twin of
 * the backend's `ComponentApplicationProvidedPorts`: one lollipop facing the Framework, one facing
 * Infrastructure.
 *
 * ⚠ ASSIGNED IN THE CONSTRUCTOR BODY, NOT AS FIELD INITIALISERS. The frontend's `tsconfig.json` sets
 * `useDefineForClassFields`, so an initialiser would run before `surfaces` exists. The fields are
 * DECLARED, which fixes their order for `describePorts`, and assigned where `surfaces` is in scope.
 *
 * ⚠ WALKED BECAUSE THIS CLASS EXTENDS `PortHolder`; a port must be an own field of exactly one holder.
 */
export class ComponentFrontendApplicationProvidedPorts extends PortHolder {
  /** Everything React may ask of the Application. */
  readonly clientUseCasesPort: ProvidedPort<IPortClientUseCases>;
  /** Where Infrastructure delivers the server's events, one call per event. */
  readonly clientServerEventsPort: ProvidedPort<IPortClientServerEvents>;

  constructor(surfaces: TApplicationSurfaces) {
    super();
    this.clientUseCasesPort = new ProvidedPort<IPortClientUseCases>('ClientUseCases', new ClientUseCasesPort(surfaces), ClientUseCasesContract);
    // One delivery seam, three takers: the consent platform's changes go to the legal side (FE4b), the
    // account's pushed credits to the payment side (FE4c), the rest to the table.
    const events: IPortClientServerEvents = { deliver: (ctx, event) => routed(surfaces, ctx, event) };
    this.clientServerEventsPort = new ProvidedPort<IPortClientServerEvents>('ClientServerEvents', events, ClientServerEventsContract);
  }
}

function routed(surfaces: TApplicationSurfaces, ctx: Parameters<IPortClientServerEvents['deliver']>[0], event: IPayloadClientServerEvent) {
  if (event.type === 'consentChanged') return surfaces.legal.consentChanged(ctx, event.payload);
  if (event.type === 'creditsUpdated') return surfaces.payment.creditsUpdated(ctx, event.payload);
  const tableEvent: TTableEvent = event;
  return surfaces.table.deliver(ctx, tableEvent);
}
